import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { Attachment } from "@/entities/attachment";
import type { ThreadDetailPost } from "@/entities/post";
import type { Thread } from "@/entities/thread";
import type { ViewedItem } from "@/features/history/stores/historyStore";
import { ApiError, apiGet } from "@/shared/api";
import { useCurrentBoardSlug } from "@/shared/hooks/useCurrentBoardSlug";
import { createLimit } from "@/shared/lib/concurrencyLimit";

interface ThreadDetailData {
  thread: Thread;
  counts: number;
  posts: ThreadDetailPost[];
}

export interface HistoryThreadEntry {
  thread: Thread;
  counts: number;
  /** OP (posts[0]) の最初の添付。getImageUrl でサムネイル/フル画像を取得 */
  firstAttachment: Attachment | null;
  /** どの板のスレッドか（Link 構築用に呼び出し側に返す） */
  boardSlug: string;
}

/**
 * 履歴ビュー用の並列スレッド取得フック。
 *
 * 旧 API の `/threads?ids=...` 一括取得が新 API では存在しないため、
 * 各 ViewedItem に対して `/threads/{boardSlug}/{threadId}` を p-limit(5) で
 * 並列フェッチし、Promise.allSettled で集約する。
 * 失敗分は無視し、成功分のみを viewedItems の入力順を保って返す。
 *
 * board routing 対応 (PR-0): boardSlug を per-entry で持つように変更。
 * v1 以前のエントリ (boardSlug 空文字) は現在の board へ fallback。
 */
export function useHistoryThreads(
  viewedItems: ViewedItem[],
  enabled: boolean,
): UseQueryResult<HistoryThreadEntry[]> {
  const currentSlug = useCurrentBoardSlug();

  const queryKey = [
    "history-threads",
    currentSlug,
    viewedItems.map((v) => `${v.boardSlug || currentSlug}:${v.id}`).join(","),
  ];

  return useQuery({
    queryKey,
    queryFn: async (): Promise<HistoryThreadEntry[]> => {
      if (viewedItems.length === 0) return [];
      const limit = createLimit(5);
      const tasks = viewedItems.map((item) =>
        limit(async (): Promise<HistoryThreadEntry | null> => {
          const slug = item.boardSlug || currentSlug;
          try {
            const data = await apiGet<ThreadDetailData>(
              `/threads/${slug}/${item.id}`,
            );
            const firstAttachment = data.posts[0]?.attachments?.[0] ?? null;
            return {
              thread: data.thread,
              counts: data.counts,
              firstAttachment,
              boardSlug: slug,
            };
          } catch (err) {
            // 削除済み等で 404 の場合のみ null を返して履歴から落とす。
            // それ以外 (network / 5xx) は throw して TanStack Query の retry に任せる。
            if (err instanceof ApiError && err.status === 404) {
              return null;
            }
            console.warn("history thread fetch failed", { item, err });
            throw err;
          }
        }),
      );
      const results = await Promise.all(tasks);
      return results.filter((v): v is HistoryThreadEntry => v != null);
    },
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
}
